const STORAGE_KEY = "taistu_blu_thread_v1";
const MAX_BYTES = 4_500_000;
const SCHEMA_V2 = 2;

function newThreadId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `t_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export function deriveBluThreadTitle(messages) {
  const u = (messages || []).find((m) => m.role === "user" && String(m.content || "").trim());
  if (!u) return "New chat";
  const raw = String(u.content).trim().replace(/\s+/g, " ");
  if (raw.length <= 52) return raw;
  return `${raw.slice(0, 52)}…`;
}

function normalizeMessages(raw) {
  if (!Array.isArray(raw)) return null;
  const out = [];
  for (const m of raw) {
    if (!m || (m.role !== "user" && m.role !== "assistant")) continue;
    if (typeof m.content !== "string") continue;
    const row = { role: m.role, content: m.content };
    if (typeof m.sentAt === "string" && m.sentAt.trim()) {
      const t = new Date(m.sentAt).getTime();
      if (!Number.isNaN(t)) row.sentAt = m.sentAt;
    }
    out.push(row);
  }
  return out.length ? out : null;
}

function migrateV1ToPack(data) {
  if (!data || typeof data !== "object") return null;
  if (data.version === SCHEMA_V2 && Array.isArray(data.threads) && data.threads.length) return data;
  const msgs = normalizeMessages(data.messages);
  if (!msgs) return null;
  const id = newThreadId();
  return {
    version: SCHEMA_V2,
    activeThreadId: id,
    threads: [
      {
        id,
        title: deriveBluThreadTitle(msgs),
        messages: msgs,
        updatedAt: typeof data.updatedAt === "string" ? data.updatedAt : new Date().toISOString(),
      },
    ],
  };
}

function readRaw() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeRaw(obj) {
  try {
    const payload = JSON.stringify(obj);
    if (payload.length > MAX_BYTES) return;
    localStorage.setItem(STORAGE_KEY, payload);
  } catch {
    /* quota / private mode */
  }
}

/** Full thread pack (v2). */
export function loadBluPack() {
  const raw = readRaw();
  if (!raw) return null;
  const migrated = migrateV1ToPack(raw);
  if (migrated && migrated !== raw) writeRaw(migrated);
  return migrated || migrateV1ToPack(raw);
}

export function saveBluPack(pack) {
  if (!pack || pack.version !== SCHEMA_V2 || !Array.isArray(pack.threads)) return;
  writeRaw(pack);
}

/** Messages for the active thread (used by BLUAgent + bluChatRunner). */
export function loadBluMessages() {
  const pack = loadBluPack();
  if (!pack) return null;
  const t = pack.threads.find((x) => x.id === pack.activeThreadId);
  return normalizeMessages(t?.messages);
}

export function saveBluMessages(messages) {
  const normalized = normalizeMessages(messages);
  if (!normalized) return;
  let pack = loadBluPack();
  if (!pack) {
    pack = createBluPack(normalized);
    if (pack) saveBluPack(pack);
    return;
  }
  const idx = pack.threads.findIndex((t) => t.id === pack.activeThreadId);
  if (idx < 0) return;
  const threads = [...pack.threads];
  const prev = threads[idx];
  threads[idx] = {
    ...prev,
    messages: normalized,
    updatedAt: new Date().toISOString(),
    title: prev.customTitle ? prev.title : deriveBluThreadTitle(normalized),
  };
  saveBluPack({ ...pack, threads });
}

export function clearBluMessages() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Create default pack with initial assistant messages (caller supplies greeting rows). */
export function createBluPack(greetingMessages) {
  const norm = normalizeMessages(greetingMessages);
  if (!norm?.length) return null;
  const id = newThreadId();
  const now = new Date().toISOString();
  return {
    version: SCHEMA_V2,
    activeThreadId: id,
    threads: [
      {
        id,
        title: deriveBluThreadTitle(norm),
        messages: norm,
        updatedAt: now,
      },
    ],
  };
}

export function bluPackSetActive(pack, activeThreadId) {
  if (!pack?.threads?.some((t) => t.id === activeThreadId)) return pack;
  return { ...pack, activeThreadId };
}

/** Append a new thread and switch to it. */
export function bluPackAddThread(pack, greetingMessages) {
  const norm = normalizeMessages(greetingMessages);
  if (!norm?.length) return pack;
  const id = newThreadId();
  const now = new Date().toISOString();
  const thread = {
    id,
    title: deriveBluThreadTitle(norm),
    messages: norm,
    updatedAt: now,
  };
  const prevThreads = pack?.version === SCHEMA_V2 && Array.isArray(pack.threads) ? pack.threads : [];
  return {
    version: SCHEMA_V2,
    activeThreadId: id,
    threads: [...prevThreads, thread],
  };
}

/** Replace active thread messages (e.g. Clear chat). */
export function bluPackResetActiveThread(pack, greetingMessages) {
  const norm = normalizeMessages(greetingMessages);
  if (!norm?.length) return pack;
  const idx = pack.threads.findIndex((t) => t.id === pack.activeThreadId);
  if (idx < 0) return pack;
  const threads = [...pack.threads];
  const now = new Date().toISOString();
  threads[idx] = {
    ...threads[idx],
    messages: norm,
    title: "New chat",
    updatedAt: now,
  };
  return { ...pack, threads };
}
